import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { isAfter, isBefore, subMonths, addMonths, format } from 'date-fns';
import Decimal from 'decimal.js';
import { capitalize, compact } from 'lodash-es';
import { convertStrToRichText } from 'seagull';
import { getAutoApprovalRange, isAutoApprovalValid } from '~/business-logic/autoApprovalLogic';
import { getPriceIncreaseDateRange } from '~/business-logic/priceIncreaseLogic';
import { createErrorObject, isBoolTrue } from '~/components/ImportWizard/ImportWizard.utils';
import { netTermsOptions } from '~/constants';
import { decimalFromStr } from '~/infra/utils';
import { serviceModel, DiscountModel, serviceCostModel, serviceBillingModel, BillingDayOfMonthModel } from '~/models';
import { PriceIncreaseModel, RoundPrices } from '~/models/PriceIncreaseModel';
import { ServiceSource } from '~/models/service/model';
import { makeBillUpfrontState, makeMaxCharges, makeOnApprovalBilling, makeOneTimeManualBilling, makeOngoingBilling, makeRepeatableManualBilling } from '~/models/service-billing';
import { makeFixedCost, makeRangeCost, makeVariableCost } from '~/models/service-cost';
import { fromDateStampObject, makeDateStampObject } from '~/models/time';
import { isRowOfBundle } from '../ImportServices/servicesValidation.utils';
const netTermsOptionsListFormatter = new Intl.ListFormat(undefined, {
    type: 'disjunction'
});
export const importAgreementsTextsPath = 'pages.importAgreements';
export const importAgreementsValidationTextsPath = 'pages.importAgreements.validation';
export const makeAgreementsErrorMessages = (importAgreementsValidationTexts)=>({
        getInvalidBooleanError: (_fieldName)=>importAgreementsValidationTexts.invalidBooleanError(),
        billingDayError: (serviceType)=>importAgreementsValidationTexts.billingDayError({
                serviceType
            }),
        priceIncreaseOnlyForRecurringError: (serviceType)=>importAgreementsValidationTexts.priceIncreaseOnlyForRecurringError({
                serviceType
            }),
        paymentTermsError: importAgreementsValidationTexts.paymentTermsError({
            options: netTermsOptionsListFormatter.format(netTermsOptions.map(String))
        }),
        accountantAccessError: importAgreementsValidationTexts.accountantAccessError(),
        optionalServiceError: importAgreementsValidationTexts.optionalServiceError(),
        sameAgreementUnmatchingDataError: importAgreementsValidationTexts.sameAgreementUnmatchingDataError(),
        termsAndConditionsError: importAgreementsValidationTexts.termsAndConditionsError(),
        billUpfrontLastDayError: importAgreementsValidationTexts.billUpfrontLastDayError(),
        singlePackageError: importAgreementsValidationTexts.singlePackageError(),
        tooManyPackagesError: importAgreementsValidationTexts.tooManyPackagesError(),
        packageUnassignedServiceError: importAgreementsValidationTexts.packageUnassignedServiceError(),
        introVideoUrlError: importAgreementsValidationTexts.introVideoUrlError(),
        recipientDetailsIncompleteError: importAgreementsValidationTexts.recipientDetailsIncompleteError(),
        duplicateRecipientEmailError: importAgreementsValidationTexts.duplicateRecipientEmailError(),
        mainEmailSameAsRecipientError: importAgreementsValidationTexts.mainEmailSameAsRecipientError(),
        autoApprovalRangeError: (data)=>importAgreementsValidationTexts.autoApprovalRangeError(data),
        invalidDateFormatError: importAgreementsValidationTexts.invalidDateFormatError(),
        effectiveDateOutOfRangeError: importAgreementsValidationTexts.effectiveDateOutOfRangeError(),
        dateOutOfRangeError: (data)=>importAgreementsValidationTexts.dateOutOfRangeError(data)
    });
const recipientIndices = [
    1,
    2,
    3,
    4
];
const isInvalidRecipientFields = (fields)=>fields.some((val, _, arr)=>!!val !== !!arr[0]);
export const dayOfMonthOptions = ()=>new Array(28).fill(null).map((_, idx)=>idx + 1);
export class DateParseResult {
    valid() {
        return this.result.valid;
    }
    datestamp() {
        return this.result.valid ? this.result.date : null;
    }
    date() {
        return this.result.valid && this.result.date ? fromDateStampObject(this.result.date) : null;
    }
    error() {
        return this.result.valid ? null : this.result.error;
    }
    toValidateResult() {
        return this.result.valid ? true : {
            valid: false,
            errorType: this.result.error
        };
    }
    constructor(result){
        _define_property(this, "result", void 0);
        this.result = result;
    }
}
export const getAutoApprovalValidation = (inputName, agreementsErrorMessages)=>({
        inputName,
        required: false,
        shouldMatchSameAgreementServices: true,
        validate (value) {
            const days = +value;
            const isValidAutoApprovalPeriod = !isNaN(days) && Number.isInteger(days) && isAutoApprovalValid(days);
            return value === '' || isValidAutoApprovalPeriod;
        },
        validateError: (headerName, rowNumber)=>{
            const autoApprovalRange = getAutoApprovalRange();
            return createErrorObject(headerName, rowNumber, agreementsErrorMessages.autoApprovalRangeError({
                minDays: autoApprovalRange.minDays,
                maxDays: autoApprovalRange.maxDays
            }));
        }
    });
export function configureValidation(validator) {
    return {
        validate: (firstName)=>{
            const result = validator(firstName);
            return typeof result === 'string' ? {
                valid: false,
                errorType: result
            } : {
                valid: result
            };
        },
        validateError: (headerName, rowNumber, errorType)=>createErrorObject(headerName, rowNumber, errorType || '')
    };
}
export function validateAgreementsRecipient(recipientIndex, rowObject, agreementsErrorMessages) {
    if (isInvalidRecipientFields([
        rowObject["recipient".concat(recipientIndex, "Email")],
        rowObject["recipient".concat(recipientIndex, "FirstName")],
        rowObject["recipient".concat(recipientIndex, "LastName")],
        rowObject["recipient".concat(recipientIndex, "RequiredToSign")]
    ])) return createErrorObject("recipient ".concat(recipientIndex), rowObject.lineNumber, agreementsErrorMessages.recipientDetailsIncompleteError);
}
export function validateAgreementsRecipients(rowObject, agreementsErrorMessages) {
    const recipientEmails = compact([
        rowObject.recipient1Email,
        rowObject.recipient2Email,
        rowObject.recipient3Email,
        rowObject.recipient4Email
    ]);
    if (new Set(recipientEmails).size !== recipientEmails.length) {
        return createErrorObject('recipient emails', rowObject.lineNumber, agreementsErrorMessages.duplicateRecipientEmailError);
    }
    if (recipientEmails.includes(rowObject.email)) {
        return createErrorObject('email', rowObject.lineNumber, agreementsErrorMessages.mainEmailSameAsRecipientError);
    }
    for (const recipientIndex of recipientIndices){
        const error = validateAgreementsRecipient(recipientIndex, rowObject, agreementsErrorMessages);
        if (error) {
            return error;
        }
    }
}
export function getDraftReviewers(rowObject) {
    return compact(recipientIndices.map((recipientIndex)=>{
        const email = rowObject["recipient".concat(recipientIndex, "Email")];
        const firstName = rowObject["recipient".concat(recipientIndex, "FirstName")];
        const lastName = rowObject["recipient".concat(recipientIndex, "LastName")];
        const requiredToSign = rowObject["recipient".concat(recipientIndex, "RequiredToSign")];
        return email && firstName && lastName && requiredToSign && {
            email,
            firstName,
            lastName,
            isSignatory: isBoolTrue(requiredToSign)
        };
    }));
}
export function validatePackages(serviceRows, agreementsErrorMessages) {
    const [packageNames, hasServicesWithoutPackageName] = serviceRows.reduce((acc, rowObject)=>{
        const emptyPackageName = rowObject.packages === '';
        return [
            !emptyPackageName ? acc[0].add(rowObject.packages) : acc[0],
            acc[1] || emptyPackageName
        ];
    }, [
        new Set([]),
        false
    ]);
    if (packageNames.size === 1) {
        return createErrorObject('packages', serviceRows[0].lineNumber, agreementsErrorMessages.singlePackageError);
    }
    if (packageNames.size > 3) {
        return createErrorObject('packages', serviceRows[0].lineNumber, agreementsErrorMessages.tooManyPackagesError);
    }
    if (packageNames.size > 0 && hasServicesWithoutPackageName) {
        return createErrorObject('packages', serviceRows[0].lineNumber, agreementsErrorMessages.packageUnassignedServiceError);
    }
}
export function parseDateString(dateString, agreementsErrorMessages) {
    if (!dateString) {
        return new DateParseResult({
            valid: true,
            date: null
        });
    }
    const result1 = dateString.match(/^(?:((?:0?(?:1|3|5|7|8))|(?:10|12))[-/]((?:[012]?[0-9])|(?:3[01]))|(?:((?:0?(?:4|6|9))|(?:11))[-/]((?:[012]?[0-9])|(?:30)))|(?:(0?(?:2))[-/]([012]?[0-9])))[-/](\d{4})$/);
    if (result1) {
        const [, month1, day1, month2, day2, month3, day3, year] = result1;
        const month = month1 || month2 || month3;
        const day = day1 || day2 || day3;
        return new DateParseResult({
            valid: true,
            date: makeDateStampObject(+year, +month, +day)
        });
    }
    const result2 = dateString.match(/^(\d{4})[-/](?:((?:[012]?[0-9])|(?:3[01]))[-/]((?:0?(?:1|3|5|7|8))|(?:10|12))|(?:((?:[012]?[0-9])|(?:30))[-/]((?:0?(?:4|6|9))|(?:11)))|(?:([012]?[0-9])[-/](0?(?:2))))$/);
    if (result2) {
        const [, year, day1, month1, day2, month2, day3, month3] = result2;
        const month = month1 || month2 || month3;
        const day = day1 || day2 || day3;
        return new DateParseResult({
            valid: true,
            date: makeDateStampObject(+year, +month, +day)
        });
    }
    return new DateParseResult({
        valid: false,
        error: agreementsErrorMessages.invalidDateFormatError
    });
}
export function parseEffectiveDate(effectiveDate, now, agreementsErrorMessages) {
    const parsed = parseDateString(effectiveDate, agreementsErrorMessages);
    const date = parsed.date();
    if (date && (isBefore(date, subMonths(now, 24)) || isAfter(date, addMonths(now, 12)))) {
        return new DateParseResult({
            valid: false,
            error: agreementsErrorMessages.effectiveDateOutOfRangeError
        });
    }
    return parsed;
}
export function parsePriceIncreaseStartsOn(priceIncreaseStartsOn, effectiveDate, now, agreementsErrorMessages) {
    const parsed = parseDateString(priceIncreaseStartsOn || '', agreementsErrorMessages);
    const date = parsed.date();
    if (date) {
        const parsedEffectiveDate = parseEffectiveDate(effectiveDate, now, agreementsErrorMessages);
        const { minDate, maxDate } = getPriceIncreaseDateRange(now, parsedEffectiveDate.date());
        if (isBefore(date, minDate) || isAfter(date, maxDate)) {
            const dateFormat = (date)=>format(date, 'MM-dd-yyyy');
            return new DateParseResult({
                valid: false,
                error: agreementsErrorMessages.dateOutOfRangeError({
                    minDate: dateFormat(minDate),
                    maxDate: dateFormat(maxDate)
                })
            });
        }
    }
    return parsed;
}
function makeServiceParticipation(optionalService) {
    const normalizedOptionalService = optionalService === null || optionalService === void 0 ? void 0 : optionalService.toLowerCase().trim();
    if ([
        'included',
        'not included'
    ].includes(normalizedOptionalService)) {
        const includedByDefault = normalizedOptionalService === 'included';
        return {
            type: 'optional',
            optional: {
                includedByDefault
            }
        };
    } else {
        return {
            type: 'mandatory'
        };
    }
}
function makeServiceCost(rowObject, param) {
    let { now, agreementsErrorMessages } = param, { useBundleFields, customRate } = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {
        useBundleFields: false
    };
    const { discountType, effectiveDate, priceIncreaseRate, priceIncreaseStartsOn } = rowObject;
    const rate = customRate !== null && customRate !== void 0 ? customRate : rowObject.rate;
    const pricing = useBundleFields ? rowObject.bundlePricing : rowObject.pricing;
    const unitName = useBundleFields ? rowObject.bundleUnitName : rowObject.unitName;
    const unitCap = useBundleFields ? rowObject.bundleUnitCap : rowObject.unitCap;
    const discount = decimalFromStr(rowObject.discount);
    const priceIncrease = priceIncreaseRate ? PriceIncreaseModel.make(RoundPrices.None, new Decimal(priceIncreaseRate), parsePriceIncreaseStartsOn(priceIncreaseStartsOn, effectiveDate, now, agreementsErrorMessages).date()) : PriceIncreaseModel.makeNone();
    const discountObject = discount != null && discount ? (discountType === null || discountType === void 0 ? void 0 : discountType.toLowerCase()) === 'flat' ? DiscountModel.makeValue(discount.toNumber()) : DiscountModel.makePercent(discount.toNumber()) : null;
    switch(pricing === null || pricing === void 0 ? void 0 : pricing.toLowerCase()){
        case 'fixed':
            {
                return makeFixedCost(decimalFromStr(rate), {
                    discount: discountObject,
                    priceIncrease
                });
            }
        case 'hourly':
        case 'quantity':
            return makeVariableCost(decimalFromStr(rate), {
                unitName,
                unitCap: decimalFromStr(unitCap) || new Decimal(0),
                discount: discountObject,
                priceIncrease
            });
        case 'range':
            {
                const [first, second] = rate.split('-').map((p)=>p.trim());
                const hasMin = Boolean(second); // when there is only one number > it is the maximum price
                return makeRangeCost(decimalFromStr(second || first), {
                    minPrice: hasMin ? decimalFromStr(first) : null,
                    discount: discountObject,
                    priceIncrease
                });
            }
        default:
            throw new Error('bad service pricing configuration');
    }
}
export function getIsRecurring(rowObject) {
    var _this;
    return ((_this = isRowOfBundle(rowObject) ? rowObject.bundleBillingOccurrence : rowObject.billingOccurrence) === null || _this === void 0 ? void 0 : _this.toLowerCase()) !== 'one time';
}
export function getIsPaused(rowObject) {
    return isBoolTrue(rowObject.startPaused || '');
}
export function getSkipBillingOnAcceptance(rowObject) {
    return !isBoolTrue(rowObject.issueInvoiceOnAcceptance || '');
}
function makeServiceBilling(rowObject) {
    const { billedUpfront, automaticBilling, billingDay } = rowObject;
    const billingOccurrence = isRowOfBundle(rowObject) ? rowObject.bundleBillingOccurrence : rowObject.billingOccurrence;
    const isRecurring = getIsRecurring(rowObject);
    const isAutomaticBilling = isBoolTrue(automaticBilling);
    const isMonthly = 'monthly' === billingOccurrence.toLowerCase();
    const isWeekly = [
        'weekly',
        'biweekly'
    ].includes(billingOccurrence.toLowerCase());
    const isBilledUpfront = isBoolTrue(billedUpfront);
    const billingDayOfMonth = isMonthly ? makeBillingDayOfMonth(billingDay) : null;
    const billingDayOfWeek = isWeekly ? capitalize(billingDay) : null;
    const recurrencePeriod = billingOccurrence.toLowerCase();
    const isPaused = getIsPaused(rowObject);
    const skipBillingOnAcceptance = getSkipBillingOnAcceptance(rowObject);
    if (isAutomaticBilling && !isRecurring) {
        return makeOnApprovalBilling();
    }
    if (!isAutomaticBilling && !isRecurring) {
        return makeOneTimeManualBilling();
    }
    if (!isAutomaticBilling && isRecurring) {
        return makeRepeatableManualBilling(recurrencePeriod, {
            maxCharges: makeMaxCharges(false)
        });
    }
    return makeOngoingBilling(recurrencePeriod, {
        isAwaitingApproval: true,
        billingDayOfMonth,
        billingDayOfWeek,
        maxCharges: makeMaxCharges(false),
        isProrated: false,
        isPaused,
        isBilledUpfront,
        billUpfrontState: makeBillUpfrontState(skipBillingOnAcceptance)
    });
}
function makeBillingDayOfMonth(billingDay) {
    return BillingDayOfMonthModel.make(billingDay.toLowerCase() === 'last' ? 'last' : parseInt(billingDay));
}
export const createServiceFromCsvRow = (rowObject, context)=>{
    const { serviceName, serviceDescription, optionalService } = rowObject;
    const { generateUniqueId } = context;
    return {
        id: generateUniqueId('service'),
        name: serviceName,
        description: convertStrToRichText(serviceDescription),
        terms: '',
        billing: serviceBillingModel.toDTO(makeServiceBilling(rowObject)),
        cost: serviceCostModel.toDTO({
            cost: makeServiceCost(rowObject, context),
            shouldResetDiscount: false
        }),
        participation: makeServiceParticipation(optionalService),
        isBillable: false,
        serviceTemplateId: generateUniqueId('service-template'),
        status: serviceModel.statusToDTO(serviceModel.makeAwaitingApprovalStatus(), makeServiceCost(rowObject, context)),
        source: ServiceSource.Draft
    };
};
function createIncludedServiceFromCsvRow(rowObject, context) {
    var _price_price;
    const { generateUniqueId } = context;
    const price = serviceCostModel.toDTO({
        cost: makeServiceCost(rowObject, context),
        shouldResetDiscount: true
    });
    return {
        id: generateUniqueId('included-service'),
        name: rowObject.serviceName,
        description: convertStrToRichText(rowObject.serviceDescription),
        terms: '',
        rate: ((_price_price = price.price) !== null && _price_price !== void 0 ? _price_price : price.maxPrice) * 100,
        serviceTemplateId: null
    };
}
export function createServiceBundleFromCsvRow(rowObjects, context) {
    const firstRow = rowObjects[0];
    const { bundleName, bundleDescription, optionalService } = firstRow;
    const { generateUniqueId } = context;
    let accumulatedCost = 0;
    const includedServicesToCreateAsServices = [];
    const includedServices = [];
    rowObjects.forEach((row)=>{
        const includedService = createIncludedServiceFromCsvRow(row, context);
        accumulatedCost += includedService.rate;
        includedServicesToCreateAsServices.push(createServiceFromCsvRow(_object_spread_props(_object_spread({}, row), {
            discount: '',
            discountType: '',
            automaticBilling: 'no'
        }), context));
        includedServices.push(includedService);
    });
    const bundleService = {
        id: generateUniqueId('service'),
        name: bundleName,
        description: convertStrToRichText(bundleDescription || ''),
        terms: '',
        billing: serviceBillingModel.toDTO(makeServiceBilling(firstRow)),
        cost: serviceCostModel.toDTO({
            cost: makeServiceCost(firstRow, context, {
                useBundleFields: true,
                customRate: accumulatedCost.toString()
            }),
            shouldResetDiscount: false
        }),
        participation: makeServiceParticipation(optionalService),
        isBillable: false,
        serviceTemplateId: generateUniqueId('service-template'),
        status: serviceModel.statusToDTO(serviceModel.makeAwaitingApprovalStatus(), makeServiceCost(firstRow, context)),
        source: ServiceSource.Draft,
        bundle: {
            services: includedServices
        }
    };
    return {
        bundleService,
        includedServicesToCreateAsServices
    };
}
